import { useMemo } from 'react';

import { type Item, SelectionCategories } from '@revelio/iso-utility';

import type { NormalizedSelectionListItem, SelectionListItems } from '../types';
import {
  nestSelectionLists,
  type TreeData,
} from '../utils/selectionListTreeUtils';
import { useSelectionLists } from './useSelectionLists';

interface UseCompanyPlaceholderTreeOptions {
  /** Optional filter applied to company items before nesting. */
  companyFilterFn?: (item: Item) => boolean;
}

/**
 * Fetches rics_k10, rics_k50, and company selection lists, nests them into a
 * `TreeData[]` tree, and sorts "Other" to the bottom of the root level.
 *
 * An optional `companyFilterFn` can be provided to pre-filter company items
 * (and prune empty parent groups) before nesting.
 */
export const useCompanyPlaceholderTree = (
  options?: UseCompanyPlaceholderTreeOptions
): TreeData[] => {
  const selectionLists = useSelectionLists(['rics_k10', 'rics_k50', 'company']);

  const companyFilterFn = options?.companyFilterFn;

  return useMemo(() => {
    if (!selectionLists) return [];

    const ricsK10 = selectionLists.rics_k10;
    const ricsK50 = selectionLists.rics_k50;
    const company = selectionLists.company;

    if (!ricsK10?.length || !ricsK50?.length || !company?.length) return [];

    // Apply company filter if provided
    const filteredCompany = companyFilterFn
      ? company.filter((item) => companyFilterFn(item as unknown as Item))
      : company;

    let filteredRicsK50 = ricsK50;
    let filteredRicsK10 = ricsK10;

    if (companyFilterFn) {
      // Prune rics_k50 groups that have no remaining companies
      const k50WithCompanies = new Set<string>();
      for (const c of filteredCompany) {
        const k50Id = (c as Record<string, unknown>).rics_k50;
        if (k50Id != null) k50WithCompanies.add(String(k50Id));
      }
      filteredRicsK50 = ricsK50.filter((item) =>
        k50WithCompanies.has(String(item.id))
      );

      // Prune rics_k10 groups that have no remaining rics_k50
      const k10WithK50 = new Set<string>();
      for (const item of filteredRicsK50) {
        const k10Id = (item as Record<string, unknown>).rics_k10;
        if (k10Id != null) k10WithK50.add(String(k10Id));
      }
      filteredRicsK10 = ricsK10.filter((item) =>
        k10WithK50.has(String(item.id))
      );
    }

    const normalizeItems = (
      items: readonly { [key: string]: unknown }[]
    ): NormalizedSelectionListItem[] =>
      items.map(
        (item) =>
          ({ ...item, id: String(item.id) }) as NormalizedSelectionListItem
      );

    const input: SelectionListItems[] = [
      {
        id: SelectionCategories.RICS_K10,
        value: normalizeItems(filteredRicsK10),
      },
      {
        id: SelectionCategories.RICS_K50,
        value: normalizeItems(filteredRicsK50),
      },
      {
        id: SelectionCategories.COMPANY,
        value: normalizeItems(filteredCompany),
      },
    ];

    const nested = nestSelectionLists(input);

    // Sort "Other" to bottom at root level
    nested.sort((a, b) => {
      const aIsOther = a.name === 'Other';
      const bIsOther = b.name === 'Other';
      if (aIsOther !== bIsOther) return aIsOther ? 1 : -1;
      return 0;
    });

    return nested;
  }, [selectionLists, companyFilterFn]);
};
